/**
 * ZLayers — the app's single z-index authority.
 *
 * Every floating surface belongs to a named band; bands are ordered and
 * spaced far apart so a whole family of elements (e.g. stacked modals)
 * fits inside its band without ever crossing into the next one. Nothing
 * outside this file should invent a raw z value for a new surface —
 * pick a band, or add one here.
 *
 * Ordering (bottom → top):
 *   page       — in-page popovers, dropdowns, hover cards
 *   sticky     — navbars, rails, sticky headers
 *   overlay    — full-screen page overlays (global search, drag-drop)
 *   drawer     — slide-in drawers (ShareDrawer legacy value)
 *   modal      — dialog stack (ModalStack allocates inside this band)
 *   modalTop   — surfaces that must beat every dialog: a dropdown or
 *                toast that belongs to the topmost dialog
 *   system     — system-level alerts (PromptDialog & co. at max int)
 *
 * The absolute numbers keep the pre-existing values (ShareDrawer at 1e9,
 * ModalStack at 1.05e9, system alerts at max int) so migrating a surface
 * to a band never changes what it renders above or below.
 */

export const Z = {
  page: 10000,
  sticky: 100000,
  overlay: 500000,
  drawer: 1000000000,
  modal: 1050000000,
  modalTop: 2000000000,
  system: 2147483647,
}

// Spacing between allocations inside a band (stacked modals, queued toasts).
export const Z_STEP = 10

const counters = {}

/**
 * Monotonic allocation inside a band — for families that stack (modals).
 * Returns band base + n*step. Callers that unmount/remount freely can
 * keep using their own stack logic (ModalStack does) — this is for
 * simple "give me the next slot" cases.
 */
export function allocate(band) {
  if (!(band in Z)) throw new Error(`ZLayers: unknown band "${band}"`)
  counters[band] = (counters[band] || 0) + 1
  return Z[band] + counters[band] * Z_STEP
}

export default Z
